import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Session } from '@supabase/supabase-js';

interface Organization {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  owner_id: string;
  plan_type: 'team_free' | 'team_pro' | 'team_enterprise';
  max_members: number;
  created_at: string;
}

interface OrganizationMember {
  id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joined_at: string;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  };
}

interface OrganizationInvitation {
  id: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  expires_at: string;
  created_at: string;
  invited_by: string;
}

interface TeamManagementProps {
  session: Session;
}

export const TeamManagement: React.FC<TeamManagementProps> = ({ session }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Form states
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');

  // Fetch user's organizations
  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          organization_id,
          role,
          organizations (
            id,
            name,
            slug,
            description,
            owner_id,
            plan_type,
            max_members,
            created_at
          )
        `)
        .eq('user_id', session.user.id);

      if (error) throw error;

      const orgs = data
        ?.map((item: any) => item.organizations)
        .filter((org: any): org is Organization => org !== null) || [];
      
      setOrganizations(orgs);
      
      if (orgs.length > 0 && !selectedOrg) {
        setSelectedOrg(orgs[0]);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch organization members
  const fetchMembers = async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          id,
          user_id,
          role,
          joined_at,
          profiles!organization_members_user_id_fkey (
            first_name,
            last_name,
            email
          )
        `)
        .eq('organization_id', orgId)
        .order('joined_at', { ascending: true });

      if (error) throw error;
      
      // Transform the data to match OrganizationMember type
      const transformedMembers: OrganizationMember[] = (data || []).map((member: any) => ({
        id: member.id,
        user_id: member.user_id,
        role: member.role,
        joined_at: member.joined_at,
        profiles: Array.isArray(member.profiles) ? member.profiles[0] : member.profiles
      }));
      
      setMembers(transformedMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  // Fetch pending invitations
  const fetchInvitations = async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from('organization_invitations')
        .select('*')
        .eq('organization_id', orgId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvitations(data || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [session.user.id]);

  useEffect(() => {
    if (selectedOrg) {
      fetchMembers(selectedOrg.id);
      fetchInvitations(selectedOrg.id);
    }
  }, [selectedOrg]);

  // Create organization
  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { error } = await supabase.functions.invoke('manage-team', {
        body: {
          action: 'create',
          name: orgName,
          description: orgDescription || undefined,
          plan_type: 'team_free'
        }
      });

      if (error) throw error;

      setShowCreateModal(false);
      setOrgName('');
      setOrgDescription('');
      fetchOrganizations();
    } catch (error) {
      console.error('Error creating organization:', error);
      alert('Failed to create organization');
    }
  };

  // Invite member
  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrg) return;

    try {
      const { error } = await supabase.functions.invoke('manage-team', {
        body: {
          action: 'invite',
          organization_id: selectedOrg.id,
          email: inviteEmail,
          role: inviteRole
        }
      });

      if (error) throw error;

      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchInvitations(selectedOrg.id);
      alert('Invitation sent successfully!');
    } catch (error) {
      console.error('Error inviting member:', error);
      alert('Failed to send invitation');
    }
  };

  // Remove member
  const handleRemoveMember = async (userId: string) => {
    if (!selectedOrg) return;
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      const { error } = await supabase.functions.invoke('manage-team', {
        body: {
          action: 'remove-member',
          organization_id: selectedOrg.id,
          user_id: userId
        }
      });

      if (error) throw error;
      fetchMembers(selectedOrg.id);
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member');
    }
  };

  // Update member role
  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'member' | 'viewer') => {
    if (!selectedOrg) return;

    try {
      const { error } = await supabase.functions.invoke('manage-team', {
        body: {
          action: 'update-member-role',
          organization_id: selectedOrg.id,
          user_id: userId,
          role: newRole
        }
      });

      if (error) throw error;
      fetchMembers(selectedOrg.id);
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Failed to update role');
    }
  };

  const getUserRole = (orgId: string): string => {
    const membership = organizations.find(o => o.id === orgId);
    return membership ? 'admin' : 'member'; // Simplified
  };

  const canManageMembers = selectedOrg && ['owner', 'admin'].includes(getUserRole(selectedOrg.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-4 border-luna-secondary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-luna-text-light dark:text-luna-text-dark">Team Management</h1>
          <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mt-1">
            Manage your organizations and team members
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-luna-secondary text-luna-primary rounded-lg font-semibold hover:opacity-90 transition-opacity"
        >
          <span className="material-symbols-outlined">add</span>
          Create Organization
        </button>
      </div>

      {/* Organization Selector */}
      {organizations.length > 0 ? (
        <div className="mb-6">
          <select
            value={selectedOrg?.id || ''}
            onChange={(e) => {
              const org = organizations.find(o => o.id === e.target.value);
              setSelectedOrg(org || null);
            }}
            className="w-full max-w-md px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
          >
            {organizations.map(org => (
              <option key={org.id} value={org.id}>
                {org.name} ({org.plan_type})
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20">
          <span className="material-symbols-outlined text-6xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4 block">groups</span>
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70 mb-4">You don't have any organizations yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-2 bg-luna-secondary text-luna-primary rounded-lg font-semibold hover:opacity-90 transition-opacity"
          >
            Create Your First Organization
          </button>
        </div>
      )}

      {selectedOrg && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Members Section */}
          <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">
                Members ({members.length}/{selectedOrg.max_members})
              </h2>
              {canManageMembers && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-luna-secondary/10 text-luna-secondary rounded-lg text-sm font-medium hover:bg-luna-secondary/20 transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">person_add</span>
                  Invite
                </button>
              )}
            </div>

            <div className="space-y-3">
              {members.map(member => {
                const profile = member.profiles as any;
                const displayName = profile?.first_name && profile?.last_name
                  ? `${profile.first_name} ${profile.last_name}`
                  : profile?.email || 'Unknown User';

                return (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-luna-secondary/20 flex items-center justify-center">
                        <span className="material-symbols-outlined text-luna-secondary">person</span>
                      </div>
                      <div>
                        <p className="font-medium text-luna-text-light dark:text-luna-text-dark">{displayName}</p>
                        <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">{profile?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canManageMembers && member.role !== 'owner' ? (
                        <select
                          value={member.role}
                          onChange={(e) => handleUpdateRole(member.user_id, e.target.value as any)}
                          className="px-2 py-1 text-sm border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded bg-transparent"
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      ) : (
                        <span className="px-3 py-1 text-xs font-semibold bg-luna-secondary/20 text-luna-secondary rounded-full">
                          {member.role}
                        </span>
                      )}
                      {canManageMembers && member.role !== 'owner' && (
                        <button
                          onClick={() => handleRemoveMember(member.user_id)}
                          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                          title="Remove member"
                        >
                          <span className="material-symbols-outlined text-sm">person_remove</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pending Invitations Section */}
          <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6">
            <h2 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark mb-4">
              Pending Invitations ({invitations.length})
            </h2>
            
            {invitations.length > 0 ? (
              <div className="space-y-3">
                {invitations.map(invitation => (
                  <div key={invitation.id} className="flex items-center justify-between p-3 bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg">
                    <div>
                      <p className="font-medium text-luna-text-light dark:text-luna-text-dark">{invitation.email}</p>
                      <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                        Invited as {invitation.role} • Expires {new Date(invitation.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400 rounded">
                      Pending
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-luna-text-light/50 dark:text-luna-text-dark/50 py-8">
                No pending invitations
              </p>
            )}
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-luna-primary rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">Create Organization</h2>
            <form onSubmit={handleCreateOrganization}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                  Organization Name *
                </label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-bg-dark text-luna-text-light dark:text-luna-text-dark"
                  placeholder="Acme Law Firm"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                  Description
                </label>
                <textarea
                  value={orgDescription}
                  onChange={(e) => setOrgDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-bg-dark text-luna-text-light dark:text-luna-text-dark"
                  placeholder="Brief description of your organization"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg font-medium hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-luna-secondary text-luna-primary rounded-lg font-semibold hover:opacity-90 transition-opacity"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-luna-primary rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">Invite Member</h2>
            <form onSubmit={handleInviteMember}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-bg-dark text-luna-text-light dark:text-luna-text-dark"
                  placeholder="colleague@example.com"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                  Role *
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as any)}
                  className="w-full px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-bg-dark text-luna-text-light dark:text-luna-text-dark"
                >
                  <option value="viewer">Viewer (Read-only)</option>
                  <option value="member">Member (Can create & edit)</option>
                  <option value="admin">Admin (Full management)</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg font-medium hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-luna-secondary text-luna-primary rounded-lg font-semibold hover:opacity-90 transition-opacity"
                >
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
